import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ReactComponent as DropdownArrow } from "../../assets/icons/dropdownArrow.svg";

export const sectionTitleClassName = "font-semibold text-secondary";
export const fieldLabelClassName =
  "block text-secondary text-sm font-normal mb-1";
export const helperTextClassName = "text-sm font-normal text-secondary";
export const sectionClassName = "border-b border-[#E2E2E2] pt-2 pb-6";

/** Red asterisk for required fields / section titles. */
export const RequiredMark: React.FC = () => (
  <span
    className="ml-0.5 align-super text-[10px] font-semibold leading-none text-red-600"
    aria-hidden="true"
  >
    *
  </span>
);

export const FieldLabel: React.FC<{
  children: React.ReactNode;
  required?: boolean;
  className?: string;
  as?: "label" | "p" | "h2" | "span";
}> = ({ children, required = false, className, as: Tag = "label" }) => (
  <Tag className={className || fieldLabelClassName}>
    {children}
    {required ? <RequiredMark /> : null}
  </Tag>
);

/** Figma Select Style field: 207×32, radius 8, pad 4×8, border #737373 */
export const FIELD_CONTROL_WIDTH_PX = 207;
export const FIELDS_ROW_GAP_PX = 24; // gap-x-6
/** Genre + Market + Category + Season (4 × 207 + 3 × 24). */
export const SELECT_INPUTS_ROW_WIDTH_PX =
  4 * FIELD_CONTROL_WIDTH_PX + 3 * FIELDS_ROW_GAP_PX;
export const FIELD_CONTROL_WIDTH_CLASS =
  "w-full min-w-0 max-w-[207px] sm:w-[207px] sm:shrink-0";
/** Style attrs row: share space evenly, cap at 207, shrink before wrapping */
export const FIELD_CONTROL_FLEX_CLASS =
  "min-w-[132px] w-full max-w-[207px] flex-1 basis-[132px]";
export const fieldsRowClassName = "flex flex-wrap gap-x-6 gap-y-4";
/** Style Type → Length stay one line until very narrow / high zoom */
export const styleAttrsRowClassName =
  "mt-4 flex flex-wrap gap-x-4 gap-y-4 min-[560px]:flex-nowrap min-[560px]:gap-x-5";
/** Fits up to 5×207px fields + gaps without over-stretching on wide screens */
export const formFieldsShellClassName = "w-full max-w-[1132px]";
/** Match Select Inputs row width (Genre/Market/Category/Season). */
export const lookFieldsShellClassName = "w-full max-w-[900px]";

export const fieldControlClassName =
  "mt-0 flex h-8 w-full min-w-0 items-center justify-between overflow-hidden rounded-[8px] border border-[#737373] bg-white py-1 pl-2 pr-8 text-left text-base";

/** Figma: h 32, radius 100, pad 4×12, gap 4, border 1 #737373, active #F7DCD3 */
export const chipClassName = (active: boolean, muted = false) =>
  `inline-flex h-8 items-center justify-center gap-1 rounded-[100px] border border-[#737373] px-3 py-1 text-xs leading-none whitespace-nowrap outline-none transition-all focus-visible:outline-none ${
    active
      ? "bg-[#F7DCD3] text-secondary cursor-pointer"
      : muted
        ? "bg-white text-secondary opacity-50 hover:opacity-100 cursor-pointer"
        : "bg-white text-secondary cursor-pointer hover:bg-gray"
  }`;

const palettePillClassName = (active: boolean, muted = false) =>
  `inline-flex h-8 w-fit max-w-full shrink-0 items-center justify-center gap-1 overflow-hidden rounded-[100px] border border-[#737373] px-2.5 py-1 transition-all ${
    active
      ? "bg-[#F7DCD3] cursor-pointer"
      : muted
        ? "bg-white opacity-50 hover:opacity-100 cursor-pointer"
        : "bg-white hover:bg-gray cursor-pointer"
  }`;

export type FieldSelectOption = {
  value: string;
  label: string;
  /** Recommended options — rendered bold in the menu. */
  emphasized?: boolean;
};

export const FieldSelect: React.FC<{
  value: string;
  options: FieldSelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Shows a filter input at the top of the open menu. */
  searchable?: boolean;
}> = ({
  value,
  options,
  onChange,
  placeholder = "Select",
  disabled,
  searchable = false,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const selected = options.find(option => option.value === value);
  const display = selected?.label || placeholder;
  const normalizedQuery = searchable ? searchQuery.trim().toLowerCase() : "";
  const visibleOptions =
    searchable && normalizedQuery
      ? options.filter(option =>
          option.label.toLowerCase().includes(normalizedQuery)
        )
      : options;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
    }
  }, [disabled]);

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      return;
    }
    if (!searchable) {
      return;
    }
    const id = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [open, searchable]);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) {
      return;
    }

    const maxMenuHeight = searchable ? 240 : 200;
    const gap = 4;
    const bottomPad = 8;

    const getScrollParent = (node: HTMLElement | null): HTMLElement | null => {
      let current = node?.parentElement || null;
      while (current && current !== document.body) {
        const style = window.getComputedStyle(current);
        const overflowY = style.overflowY;
        if (
          overflowY === "auto" ||
          overflowY === "scroll" ||
          overflowY === "overlay"
        ) {
          return current;
        }
        current = current.parentElement;
      }
      return null;
    };

    const button = buttonRef.current;
    const scrollParent = getScrollParent(button);

    const updatePosition = () => {
      const el = buttonRef.current;
      if (!el) {
        return;
      }
      const rect = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - gap;
      setMenuStyle({
        position: "fixed",
        left: rect.left,
        width: rect.width,
        top: rect.bottom + gap,
        maxHeight: Math.min(maxMenuHeight, Math.max(96, spaceBelow)),
        zIndex: 80,
      });
    };

    /** Scroll the panel down so the menu fits; never scrolls back up on close. */
    const ensureRoomBelow = () => {
      const el = buttonRef.current;
      if (!el) {
        return;
      }
      const needed = maxMenuHeight + gap + bottomPad;
      const rect = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - gap;
      if (spaceBelow >= needed) {
        return;
      }
      const delta = needed - spaceBelow;
      if (scrollParent) {
        const currentPad =
          Number.parseFloat(
            window.getComputedStyle(scrollParent).paddingBottom
          ) || 0;
        // Keep the extra pad after close so the page does not jump back up.
        scrollParent.style.paddingBottom = `${currentPad + delta}px`;
        scrollParent.scrollBy({ top: delta, behavior: "smooth" });
      } else {
        window.scrollBy({ top: delta, behavior: "smooth" });
      }
    };

    ensureRoomBelow();
    updatePosition();
    const raf = window.requestAnimationFrame(updatePosition);
    const t1 = window.setTimeout(updatePosition, 120);
    const t2 = window.setTimeout(updatePosition, 280);

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      // Intentionally do not restore scroll/padding — keep the scrolled-down position.
    };
  }, [open, options.length, searchable]);

  const listMaxHeight =
    typeof menuStyle.maxHeight === "number"
      ? Math.max(80, menuStyle.maxHeight - (searchable ? 40 : 0))
      : menuStyle.maxHeight;

  return (
    <div className="relative min-w-0" ref={dropdownRef}>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen(prev => !prev)}
        className={`${fieldControlClassName} disabled:cursor-not-allowed disabled:bg-[#F5F5F5] disabled:opacity-60`}
      >
        <span className="min-w-0 flex-1 truncate whitespace-nowrap text-left text-xs text-secondary">
          {display}
        </span>
        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <span
            className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          >
            <DropdownArrow className="w-2 h-2" />
          </span>
        </span>
      </button>
      {open &&
        !disabled &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: menuStyle.position,
              left: menuStyle.left,
              width: menuStyle.width,
              top: menuStyle.top,
              zIndex: menuStyle.zIndex,
            }}
            className="overflow-hidden rounded-lg border border-[#737373] bg-white shadow-lg"
          >
            {searchable ? (
              <div className="border-b border-[#E2E2E2] p-2">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={event => setSearchQuery(event.target.value)}
                  onClick={event => event.stopPropagation()}
                  onKeyDown={event => {
                    if (event.key === "Escape") {
                      setOpen(false);
                    }
                  }}
                  placeholder="Search material"
                  className="w-full rounded border border-[#E2E2E2] px-2 py-1.5 text-xs text-secondary placeholder:text-tertiary focus:border-[#737373] focus:outline-none"
                  aria-label="Search material"
                />
              </div>
            ) : null}
            <div
              className="dropdown-menu-scroll overflow-y-auto overflow-x-hidden"
              style={{ maxHeight: listMaxHeight }}
            >
              {visibleOptions.length === 0 ? (
                <p className="px-4 py-2 text-xs text-tertiary">No matches</p>
              ) : (
                visibleOptions.map((option, index) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`block w-full text-left px-4 py-2 text-xs text-secondary ${
                      option.emphasized ? "font-semibold" : "font-normal"
                    } ${
                      index !== visibleOptions.length - 1
                        ? "border-b border-[#E2E2E2]"
                        : ""
                    } ${
                      option.value === value ? "bg-lightest" : "hover:bg-gray"
                    }`}
                    onClick={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                  >
                    {option.label}
                  </button>
                ))
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export const FieldBlock: React.FC<{
  label: string;
  children: React.ReactNode;
  hint?: string;
  className?: string;
  required?: boolean;
}> = ({ label, children, hint, className, required = false }) => (
  <div className={`min-w-0 ${className || FIELD_CONTROL_WIDTH_CLASS}`}>
    <FieldLabel required={required}>{label}</FieldLabel>
    {children}
    {hint ? <p className="mt-1 text-xs text-red-600">{hint}</p> : null}
  </div>
);

export const ExclusivePills = <T extends string>({
  options,
  value,
  onChange,
  trailing,
  disabled,
  allowDeselect = true,
}: {
  options: Array<{ value: T; label: string }>;
  value: T | "";
  onChange: (value: T | "") => void;
  trailing?: React.ReactNode;
  disabled?: boolean;
  allowDeselect?: boolean;
}) => (
  <div className="flex flex-wrap">
    {options.map(option => {
      const isActive = value === option.value;
      const isMuted = Boolean(value) && !isActive;
      return (
        <button
          key={option.value}
          type="button"
          title={option.label}
          aria-pressed={isActive}
          disabled={disabled}
          onClick={() => {
            if (disabled) {
              return;
            }
            if (isActive) {
              if (allowDeselect) {
                onChange("");
              }
              return;
            }
            onChange(option.value);
          }}
          className={`${chipClassName(isActive, isMuted)} mr-2 mb-2 outline-none focus-visible:outline-none disabled:cursor-not-allowed`}
        >
          {option.label}
        </button>
      );
    })}
    {trailing ? (
      <span className="mr-2 mb-2 inline-flex">{trailing}</span>
    ) : null}
  </div>
);

export const pillOptions = (values: readonly string[]) =>
  values.map(value => ({ value, label: value }));

const PALETTE_SWATCH_SLOTS = 10;

const PaletteDots: React.FC<{ colors: string[]; idPrefix: string }> = ({
  colors,
  idPrefix,
}) => (
  <span className="flex w-fit items-center justify-center gap-1">
    {colors.slice(0, PALETTE_SWATCH_SLOTS).map((hex, index) => {
      const fill = String(hex || "").trim();
      const missing = !fill;
      return (
        <span
          key={`${idPrefix}-${index}`}
          title={missing ? "Pantone hex missing in pantones_new" : fill}
          className={`h-3.5 w-3.5 shrink-0 rounded-full border ${
            missing
              ? "border-dashed border-[#737373]/70 bg-transparent"
              : "border-[#737373]/25"
          }`}
          style={missing ? undefined : { backgroundColor: fill }}
        />
      );
    })}
  </span>
);

export const PaletteChip: React.FC<{
  title: string;
  colors: string[];
  idPrefix: string;
  isActive: boolean;
  isMuted: boolean;
  onClick: () => void;
  showLabel?: boolean;
  swatches?: Array<{ hex: string; name?: string; tier?: "major" | "minor" }>;
}> = ({ title, colors, idPrefix, isActive, isMuted, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={title}
    aria-pressed={isActive}
    title={title}
    className="mb-2 mr-1.5 inline-flex w-fit max-w-[7.5rem] flex-col items-center gap-1.5 outline-none focus-visible:outline-none"
  >
    <span className={palettePillClassName(isActive, isMuted)}>
      <PaletteDots idPrefix={idPrefix} colors={colors} />
    </span>
    <span className="line-clamp-2 w-full min-w-0 text-center text-[11px] font-normal italic leading-snug text-secondary">
      {title}
    </span>
  </button>
);
